import api from './api';

export interface AppNotification {
  id: number;
  type: string;
  title: string;
  body: string;
  target_url: string;
  dedupe_key: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  last_event_at: string;
}

const SHOWN_STORAGE_KEY = 'filmini_shown_notifications';
const LOGIN_BASELINE_PREFIX = 'filmini_notification_login_baseline_at';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const loadShownKeys = (): string[] => {
  try {
    const raw = localStorage.getItem(SHOWN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((value) => (typeof value === 'number' || typeof value === 'string' ? String(value) : ''))
      .filter(Boolean);
  } catch {
    return [];
  }
};

const saveShownKeys = (keys: string[]) => {
  try {
    localStorage.setItem(SHOWN_STORAGE_KEY, JSON.stringify(keys.slice(-200)));
  } catch {
    // ignore storage errors
  }
};

export const getNotificationShownKey = (notification: Pick<AppNotification, 'id' | 'last_event_at' | 'updated_at'>) =>
  `${notification.id}:${notification.last_event_at || notification.updated_at || ''}`;

const getLoginBaselineKey = (userId: number) => `${LOGIN_BASELINE_PREFIX}:${userId}`;

export const ensureNotificationLoginBaseline = (userId: number) => {
  try {
    const key = getLoginBaselineKey(userId);
    const existing = localStorage.getItem(key);
    if (existing) return existing;

    const baseline = new Date().toISOString();
    localStorage.setItem(key, baseline);
    return baseline;
  } catch {
    return new Date().toISOString();
  }
};

export const isNotificationBeforeLoginBaseline = (
  notification: Pick<AppNotification, 'created_at' | 'updated_at' | 'last_event_at'>,
  userId: number,
) => {
  const baseline = ensureNotificationLoginBaseline(userId);
  const baselineTime = Date.parse(baseline);
  const eventTime = Date.parse(notification.last_event_at || notification.updated_at || notification.created_at || '');

  return Number.isFinite(baselineTime) && Number.isFinite(eventTime) && eventTime <= baselineTime;
};

export const resolveNotificationTargetUrl = (
  notification: Pick<AppNotification, 'type' | 'target_url' | 'metadata'>
) => {
  if (
    ['media_like', 'media_comment', 'content_post'].includes(notification.type || '') &&
    Number.isFinite(Number(notification.metadata?.mediaId))
  ) {
    return `/profile?mediaId=${Number(notification.metadata?.mediaId)}`;
  }

  return notification.target_url;
};

export const hasShownNotification = (shownKey: string) => loadShownKeys().includes(shownKey);

export const rememberShownNotification = (shownKey: string) => {
  const keys = loadShownKeys();
  if (!keys.includes(shownKey)) {
    keys.push(shownKey);
    saveShownKeys(keys);
  }
};

export const pruneShownNotifications = (unreadIds: number[]) => {
  const unreadSet = new Set(unreadIds);
  const kept = loadShownKeys().filter((shownKey) => {
    const [idPart] = shownKey.split(':');
    return unreadSet.has(Number(idPart));
  });
  saveShownKeys(kept);
};

export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';

  return Notification.requestPermission();
};

export const showSystemNotification = async (notification: AppNotification) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  const targetUrl = resolveNotificationTargetUrl(notification);

  const options = {
    body: notification.body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    tag: `filmini-${notification.dedupe_key}`,
    renotify: false,
    data: { url: targetUrl },
  };

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(notification.title, options);
      return;
    } catch {
      // fall through to window notification
    }
  }

  const browserNotification = new Notification(notification.title, options);
  browserNotification.onclick = () => {
    window.focus();
    window.location.href = targetUrl;
    browserNotification.close();
  };
};

export const fetchUnreadNotifications = async (): Promise<AppNotification[]> => {
  const response = await api.get('/notifications/unread');
  return response.data.notifications || [];
};

export const markNotificationScopeRead = async (
  scope: string,
  options: { otherUserId?: number; storyId?: number; customGroupId?: number } = {}
) => {
  await api.post('/notifications/read', { scope, ...options });
};

export const syncPushSubscription = async (subscription: PushSubscription) => {
  const json = subscription.toJSON();
  await api.post('/notifications/push-subscription', {
    subscription: {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
      },
    },
  });
};

export const deletePushSubscription = async (endpoint: string) => {
  await api.delete('/notifications/push-subscription', { data: { endpoint } });
};

export const ensurePushSubscription = async () => {
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !VAPID_PUBLIC_KEY
  ) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  if (!registration.pushManager) return;

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  await syncPushSubscription(subscription);
};

export const hasActivePushSubscription = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  const registration = await navigator.serviceWorker.ready;
  if (!registration.pushManager) return false;

  const subscription = await registration.pushManager.getSubscription();
  return Boolean(subscription);
};

export const unsubscribePushNotifications = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  if (!registration.pushManager) return;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  try {
    await deletePushSubscription(subscription.endpoint);
  } catch {
    // ignore backend cleanup errors
  }

  await subscription.unsubscribe();
};
